import { DownOutlined, RightOutlined } from "@ant-design/icons";
import { Button, InputNumber, Modal } from "antd";
import moment from "moment";
import { Fragment, useState } from "react";
import { formatCurrency } from "../../../../../utils/formatCurrency";
import { useChannelRateOverride } from "./modules/useChannelRateOverride";

const CHANNEL_LABELS = {
  siteminder: "SiteMinder",
  bookingcom: "Booking.com",
  expedia: "Expedia",
  agoda: "Agoda",
  traveloka: "Traveloka",
  tripcom: "Trip.com",
};

// Renders under each rate plan row in Smart Calendar: a collapsible
// "N Connections" toggle that reveals one row per connected OTA channel.
// Each channel's rate defaults to the rate plan's own computed rate (Manual
// or Derived — read-only here, never recalculated) and can be manually
// overridden per date; overrides are stored on the ChannelRoomMapping and
// only affect what gets pushed to that one channel (see
// backend/handler/channelManager/syncRoomHandler).
export const ChannelRatesManagement = (props) => {
  const {
    mappings,
    dateColumns,
    currentMonth,
    selectedYear,
    plan,
    rentPerDayByDate,
    rentperday,
    roomId,
    onRefetchMappings,
  } = props;

  const [expanded, setExpanded] = useState(false);
  const { modalState, value, setValue, openModal, closeModal, handleSave, handleClear } =
    useChannelRateOverride({ onSaved: onRefetchMappings });

  if (!mappings || mappings.length === 0) return null;

  // Same read-only formula as RatePlansManagement's own base-rate display —
  // duplicated on purpose rather than shared/refactored, so this feature
  // cannot accidentally change how the base rate is computed.
  const getBaseRate = (dateKey) => {
    if (plan.ratePlanSetup === "Derived") {
      const baseRate = rentPerDayByDate?.[roomId]?.[dateKey] || rentperday;
      return baseRate * (1 - Number(plan.discount || 0) / 100);
    }
    if (plan.ratePlanSetup === "Manual") {
      return (
        plan.rateplanpricing.find((rpp) => rpp.roomId.toString() === roomId)
          ?.manualRate[dateKey] || 0
      );
    }
    return 0;
  };

  return (
    <Fragment>
      <tr>
        <td className="sticky-column" style={{ paddingLeft: 24, color: "#1677ff", cursor: "pointer" }}>
          <span onClick={() => setExpanded((prev) => !prev)}>
            {expanded ? <DownOutlined /> : <RightOutlined />}{" "}
            {expanded ? "Hide" : `${mappings.length} Connection${mappings.length > 1 ? "s" : ""}`}
          </span>
        </td>
        {dateColumns.map((date) => (
          <td key={`${roomId}-${date}-channels-toggle`} />
        ))}
      </tr>

      {expanded &&
        mappings.map((mapping) => (
          <tr key={mapping._id}>
            <td className="sticky-column" style={{ paddingLeft: 40 }}>
              <strong>{CHANNEL_LABELS[mapping.channel] || mapping.channel}</strong>
              <div style={{ fontSize: 11, color: "#888" }}>{mapping.roomTypeName}</div>
            </td>
            {dateColumns.map((date) => {
              const day = moment(date, "DD MMM").date();
              const dateKey = moment(
                `${day}-${currentMonth.month() + 1}-${selectedYear}`,
                "DD-MM-YYYY"
              ).format("DD-MM-YYYY");

              const override = mapping.rateOverride?.[dateKey];
              const baseRate = getBaseRate(dateKey);
              const displayRate = typeof override === "number" ? override : baseRate;

              return (
                <td
                  key={`${mapping._id}-${date}`}
                  className="availability-cell"
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    openModal({
                      mappingId: mapping._id,
                      dateKey,
                      currentValue: override,
                      baseValue: baseRate,
                      channelLabel: CHANNEL_LABELS[mapping.channel] || mapping.channel,
                    })
                  }
                >
                  <div style={typeof override === "number" ? { fontWeight: 600 } : { color: "#999", fontStyle: "italic" }}>
                    {displayRate ? formatCurrency(displayRate) : "N/A"}
                  </div>
                </td>
              );
            })}
          </tr>
        ))}

      <Modal
        title={modalState ? `${modalState.channelLabel} — ${modalState.dateKey}` : ""}
        open={!!modalState}
        onOk={handleSave}
        onCancel={closeModal}
        footer={[
          <Button key="clear" onClick={handleClear}>
            Use default rate
          </Button>,
          <Button key="cancel" onClick={closeModal}>
            Cancel
          </Button>,
          <Button key="save" type="primary" onClick={handleSave}>
            Save override
          </Button>,
        ]}
      >
        <p style={{ color: "#888" }}>
          Default rate for this date: {modalState ? formatCurrency(modalState.baseValue || 0) : ""}
        </p>
        <InputNumber
          style={{ width: "100%" }}
          min={0}
          value={value}
          onChange={(v) => setValue(v ?? "")}
          placeholder="Leave empty to use default rate"
        />
      </Modal>
    </Fragment>
  );
};
